import React, { useState } from 'react';
import axios from 'axios';
import './DaycareVideoForm.css';

const DaycareVideoForm = ({ token, onVideoSubmitted }) => {
  const [petName, setPetName] = useState('');
  const [breed, setBreed] = useState('');
  const [emotion, setEmotion] = useState('');
  const [activities, setActivities] = useState([]);
  const [numberOfDogs, setNumberOfDogs] = useState(1);
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState('');

  const BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:10000'}/api/videos`;

  // 11 available breeds
  const breeds = [
    { id: 'golden-retriever', name: 'Golden Retriever' },
    { id: 'labrador-retriever', name: 'Labrador Retriever' },
    { id: 'golden-doodle', name: 'Golden Doodle' },
    { id: 'staffordshire-bull-terrier', name: 'Staffordshire Bull Terrier' },
    { id: 'german-shepherd', name: 'German Shepherd' },
    { id: 'french-bulldog', name: 'French Bulldog' },
    { id: 'poodle', name: 'Poodle' },
    { id: 'boston-terrier', name: 'Boston Terrier' },
    { id: 'dachshund', name: 'Dachshund' },
    { id: 'west-highland-terrier', name: 'West Highland White Terrier' },
    { id: 'mixed-breed', name: 'Mixed Breed' },
  ];

  const emotions = [
    'happy',
    'excited',
    'playful',
    'calm',
    'tired',
    'proud',
  ];

  const availableActivities = [
    'played fetch',
    'went swimming',
    'learned new tricks',
    'made new friends',
    'played with toys',
    'got treats',
    'went on a walk',
    'had a nap',
  ];

  const handleActivityToggle = (activity) => {
    if (activities.includes(activity)) {
      setActivities(activities.filter(a => a !== activity));
    } else {
      setActivities([...activities, activity]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setStatus('❌ Please log in to generate videos');
      return;
    }

    setStatus('Submitting video request...');
    setIsSubmitting(true);

    try {
      const payload = {
        petName,
        breed,
        emotion,
        daycare_activities: activities,
        number_of_dogs: numberOfDogs,
      };

      const response = await axios.post(`${BASE_URL}/create`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 202) {
        const { jobId: newJobId, estimatedTime } = response.data;
        setJobId(newJobId);
        setStatus(`✅ Video generation started! Job ID: ${newJobId}. Estimated time: ${estimatedTime}`);

        // Reset form
        setPetName('');
        setBreed('');
        setEmotion('');
        setActivities([]);
        setNumberOfDogs(1);

        // Notify parent component
        if (onVideoSubmitted) {
          onVideoSubmitted(newJobId);
        }
      } else {
        setStatus('❌ Video submission failed. Try again.');
      }
    } catch (error) {
      console.error('Error submitting video request:', error);
      if (error.response?.status === 401) {
        setStatus('❌ Session expired. Please log in again.');
      } else if (error.response?.status === 403) {
        setStatus(`❌ ${error.response.data.error || 'Monthly video limit reached'}`);
      } else {
        setStatus('❌ Error submitting video request.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="video-form-container">
      <h2>🐕 Generate Pet Video</h2>
      <p className="video-form-description">
        Create an AI-powered talking pet video for your daycare customer!
      </p>

      <form onSubmit={handleSubmit}>
        <label>Pet Name: *</label>
        <input
          type="text"
          value={petName}
          onChange={(e) => setPetName(e.target.value)}
          placeholder="e.g., Max, Bella, Charlie"
          required
          disabled={isSubmitting}
        />

        <label>Breed: *</label>
        <select
          value={breed}
          onChange={(e) => setBreed(e.target.value)}
          required
          disabled={isSubmitting}
        >
          <option value="">Select Breed</option>
          {breeds.map(b => (
            <option key={b.id} value={b.name}>{b.name}</option>
          ))}
        </select>

        <label>Emotion/Mood: *</label>
        <select
          value={emotion}
          onChange={(e) => setEmotion(e.target.value)}
          required
          disabled={isSubmitting}
        >
          <option value="">Select Emotion</option>
          {emotions.map(e => (
            <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>
          ))}
        </select>

        <label>Number of Dogs:</label>
        <div className="number-selector">
          <button
            type="button"
            onClick={() => setNumberOfDogs(1)}
            className={numberOfDogs === 1 ? 'active' : ''}
            disabled={isSubmitting}
          >
            1 Dog
          </button>
          <button
            type="button"
            onClick={() => setNumberOfDogs(2)}
            className={numberOfDogs === 2 ? 'active' : ''}
            disabled={isSubmitting}
          >
            2 Dogs
          </button>
        </div>

        <label>Daycare Activities (optional):</label>
        <div className="activities-grid">
          {availableActivities.map(activity => (
            <label key={activity} className="activity-checkbox">
              <input
                type="checkbox"
                checked={activities.includes(activity)}
                onChange={() => handleActivityToggle(activity)}
                disabled={isSubmitting}
              />
              <span>{activity}</span>
            </label>
          ))}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="submit-button"
        >
          {isSubmitting ? '⏳ Generating...' : '🎬 Generate Video'}
        </button>
      </form>

      {status && (
        <div className={`status-message ${status.includes('✅') ? 'success' : 'error'}`}>
          {status}
        </div>
      )}

      {jobId && (
        <div className="job-info">
          <p>Your video is being generated. Check the Video Gallery to see the status!</p>
          <code>{jobId}</code>
        </div>
      )}
    </div>
  );
};

export default DaycareVideoForm;
